/**
 * FeedbackButtons — thumbs up / down on a specific ARIA reply.
 *
 * Optimistic UI. Persists to `/api/chat/{conv}/messages/{idx}/feedback`.
 * Second click on the already-selected value is a no-op (server keeps last value).
 * Down-vote opens a lightweight inline reason box so we capture *what* was wrong.
 */
import { useState } from "react";
import { ThumbsUp, ThumbsDown } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function FeedbackButtons({
  conversationId, messageIndex, initialValue, disabled,
}) {
  const [value, setValue] = useState(initialValue || null);
  const [showReason, setShowReason] = useState(false);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (nextValue, extraReason) => {
    if (!conversationId) {
      // Can't record feedback on messages that haven't been persisted yet
      return;
    }
    setSaving(true);
    const prev = value;
    setValue(nextValue);
    try {
      const { data } = await api.post(
        `/chat/${conversationId}/messages/${messageIndex}/feedback`,
        { value: nextValue, reason: extraReason || undefined },
      );
      const learned = (data?.learned || []).map(l => l.source_title).filter(Boolean);
      if (nextValue === "up") toast.success(learned.length ? `Thanks — ${learned.length} source(s) will rank higher next time` : "Thanks — logged as helpful", { duration: 1800 });
      if (nextValue === "down") toast.success(learned.length ? `Noted — ${learned.slice(0, 2).join(", ")} will rank lower next time` : "Thanks — noted", { duration: 2200 });
    } catch (e) {
      setValue(prev);
      toast.error("Couldn't save feedback");
    } finally {
      setSaving(false);
    }
  };

  const onUp = () => {
    if (value === "up") return;
    setShowReason(false);
    submit("up");
  };

  const onDown = () => {
    if (value === "down") {
      // Toggle the reason box back open so they can add / edit context
      setShowReason(s => !s);
      return;
    }
    submit("down");
    setShowReason(true);
  };

  const sendReason = () => {
    const r = reason.trim();
    if (!r) { setShowReason(false); return; }
    submit("down", r);
    setShowReason(false);
    setReason("");
  };

  const base = "inline-flex items-center justify-center w-7 h-7 rounded-sm border transition-colors";
  const upCls = value === "up"
    ? "text-emerald-400 border-emerald-500/50 bg-emerald-500/8"
    : "border-transparent text-[var(--aria-text-muted)] hover:text-emerald-400 hover:border-[var(--aria-border)]";
  const downCls = value === "down"
    ? "text-red-400 border-red-500/50 bg-red-500/8"
    : "border-transparent text-[var(--aria-text-muted)] hover:text-red-400 hover:border-[var(--aria-border)]";

  return (
    <div className="mt-2 flex flex-col gap-2" data-testid={`feedback-msg-${messageIndex}`}>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={onUp}
          disabled={disabled || saving}
          data-testid={`feedback-up-${messageIndex}`}
          aria-pressed={value === "up"}
          title="Helpful"
          className={`${base} ${upCls}`}
        >
          <ThumbsUp size={13} />
        </button>
        <button
          type="button"
          onClick={onDown}
          disabled={disabled || saving}
          data-testid={`feedback-down-${messageIndex}`}
          aria-pressed={value === "down"}
          title="Not helpful"
          className={`${base} ${downCls}`}
        >
          <ThumbsDown size={13} />
        </button>
      </div>
      {showReason && (
        <div className="flex items-center gap-2 fade-in" data-testid={`feedback-reason-${messageIndex}`}>
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") sendReason(); }}
            placeholder="What was wrong? (optional)"
            className="flex-1 px-2.5 py-1.5 text-xs rounded-sm border border-[var(--aria-border)] bg-[var(--aria-bg)] outline-none focus:ring-1 focus:ring-[var(--aria-primary)]"
            data-testid={`feedback-reason-input-${messageIndex}`}
            maxLength={500}
            autoFocus
          />
          <button
            onClick={sendReason}
            data-testid={`feedback-reason-submit-${messageIndex}`}
            className="px-2.5 py-1.5 text-[10px] font-mono uppercase tracking-[0.18em] border border-[var(--aria-border)] hover:border-[var(--aria-text)] rounded-sm"
          >
            Send
          </button>
          <button
            onClick={() => setShowReason(false)}
            className="px-1.5 py-1.5 text-[10px] font-mono text-[var(--aria-text-muted)] hover:text-[var(--aria-text)]"
          >
            skip
          </button>
        </div>
      )}
    </div>
  );
}
